/**
 * Debounced product search hook for the copilot.
 *
 * Follows the same pattern as useAccountSearch in useApi.ts —
 * 250ms debounce, cancel-on-unmount, loading state.
 */

import { useState, useCallback, useRef, useEffect } from "react";
import type { ProductSearchResult } from "../../services/portalProducts";
import { copilotProductService } from "../services/copilotProducts";

const DEBOUNCE_MS = 250;

export interface UseProductSearchReturn {
  results: ProductSearchResult[];
  loading: boolean;
  search: (query: string) => void;
  clear: () => void;
}

export function useProductSearch(): UseProductSearchReturn {
  const [results, setResults] = useState<ProductSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const search = useCallback((query: string) => {
    // Cancel pending request
    if (timerRef.current) clearTimeout(timerRef.current);

    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const response = await copilotProductService.searchProducts(trimmed, 10);
        if (!cancelledRef.current) {
          setResults(response.products);
        }
      } catch {
        if (!cancelledRef.current) {
          setResults([]);
        }
      } finally {
        if (!cancelledRef.current) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);
  }, []);

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setResults([]);
    setLoading(false);
  }, []);

  return { results, loading, search, clear };
}
